import { CommonPageWrapper, HeadingWrapper } from 'admin/components/wrappers';
import { PageMainLabel } from 'app/components/SHLabel';
import React, { useCallback } from 'react';
import styled from 'styled-components';
import RiderInformationTable from './RiderInformationTable';
import AdminDropDown from 'app/components/AdminDropDown';
import { findObjectById, getLookupArray } from 'utils/common';
import {
  GeneralLookup,
  GeneralLookupWrapper,
  GroupedShowRiderPointWrapper,
  RiderPoint,
  RiderSplitClass,
} from 'services/openapi';
import AdminCustomButton from 'app/components/AdminCustomButton';
import { defaultAddToShowData } from '../static';
import { TableSkeletonLoader } from 'app/components/TableSkeletonLoader';
import { FilterPayLoad } from 'admin/pages/ShowIndividual/Components/ShowViewProfile/staticData';
import SimpleReactValidator from 'simple-react-validator';

type Props = {
  lookUps: GeneralLookupWrapper[];
  showId: string | undefined;
  setPayload: React.Dispatch<React.SetStateAction<FilterPayLoad>>;
  payload: FilterPayLoad;
  setClassData: React.Dispatch<
    React.SetStateAction<GeneralLookup[] | undefined>
  >;
  getShowRiderPoints: (
    showId: string,
    divisionId?: string | undefined,
    categoryId?: string | undefined,
    classFilter?: string[] | undefined,
    sortBy?: string | undefined,
    sortOrder?: number | undefined,
  ) => void;
  setDivisionData: React.Dispatch<
    React.SetStateAction<GeneralLookup[] | undefined>
  >;
  pageSkip: number | undefined;
  divisonData: GeneralLookup[] | undefined;
  classData: GeneralLookup[] | undefined;
  loading: boolean;
  riderShowList: GroupedShowRiderPointWrapper | undefined;
  isSaving: boolean;
  setIsSaving: React.Dispatch<React.SetStateAction<boolean>>;
  hasPendingChanges: boolean;
  setHasPendingChanges: React.Dispatch<React.SetStateAction<boolean>>;
  pendingPointsRef: React.MutableRefObject<RiderPoint[]>;
  pendingScratchRef: React.MutableRefObject<Map<string, RiderSplitClass>>;
  prevLoadingRef: React.MutableRefObject<boolean>;
  isPrinting: boolean;
  setIsPrinting: React.Dispatch<React.SetStateAction<boolean>>;
  resetKey: number;
  simpleValidator: React.MutableRefObject<SimpleReactValidator>;
  triggerValidation: boolean;
};

export const ShowStatusPage = ({
  lookUps,
  showId,
  setPayload,
  payload,
  setClassData,
  getShowRiderPoints,
  setDivisionData,
  pageSkip,
  divisonData,
  classData,
  loading,
  riderShowList,
  isSaving,
  setIsSaving,
  hasPendingChanges,
  setHasPendingChanges,
  pendingPointsRef,
  pendingScratchRef,
  prevLoadingRef,
  isPrinting,
  setIsPrinting,
  resetKey,
  simpleValidator,
  triggerValidation,
}: Props) => {
  const [sortBy, setSortBy] = React.useState<string>('team');
  const [sortOrder, setSortOrder] = React.useState<number>(1);

  const handleSortChange = useCallback(
    (newSortBy: string, newSortOrder: number) => {
      setSortBy(newSortBy);
      setSortOrder(newSortOrder);
      getShowRiderPoints(
        showId ?? '',
        payload.divisionId.id,
        payload.categoryId.id,
        payload.classFilter.id ? [payload.classFilter.id] : undefined,
        newSortBy,
        newSortOrder,
      );
    },
    [showId],
  );

  // Clear isSaving only when loading transitions true → false (list reload finished)
  React.useEffect(() => {
    if (isSaving && prevLoadingRef.current && !loading) {
      setIsSaving(false);
    }
    prevLoadingRef.current = loading;
  }, [loading, isSaving]);

  // When isPrinting is true and data has finished loading, open print dialog
  React.useEffect(() => {
    if (isPrinting && !loading) {
      const afterPrint = () => {
        // Restore paginated view after the print dialog closes
        getShowRiderPoints(
          showId ?? '',
          payload.divisionId.id,
          payload.categoryId.id,
          payload.classFilter.id ? [payload.classFilter.id] : undefined,
          sortBy,
          sortOrder,
        );
        window.removeEventListener('afterprint', afterPrint);
      };
      window.addEventListener('afterprint', afterPrint);
      window.print();
      setIsPrinting(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPrinting, loading]);

  // ── Points ────────────────────────────────────────────────────────────────
  const handlePointChange = useCallback((point: RiderPoint) => {
    const prev = pendingPointsRef.current;
    const idx = prev.findIndex(p => p.showRiderId === point.showRiderId);
    if (idx >= 0) {
      const updated = [...prev];
      updated[idx] = point;
      pendingPointsRef.current = updated;
    } else {
      pendingPointsRef.current = [...prev, point];
    }
    setHasPendingChanges(true);
  }, []);

  // ── Scratch dates ─────────────────────────────────────────────────────────
  const handleScratchDateChange = useCallback((item: RiderSplitClass) => {
    // Map ensures one entry per rider; repeated edits just overwrite
    pendingScratchRef.current.set(item.showRiderId ?? '', item);
    setHasPendingChanges(true);
  }, []);

  // ── Filter helpers ────────────────────────────────────────────────────────
  const handleCategoryChange = data => {
    setPayload({
      ...payload,
      categoryId: data,
      divisionId: { id: '', name: '' },
      classFilter: { id: '', name: '' },
    });
    setClassData([]);
    const categoryLookUpList = getLookupArray('TEAM_CATEGORY', lookUps);
    const matchingObject = findObjectById(categoryLookUpList, data.id);
    if (matchingObject) {
      setDivisionData(matchingObject.children!);
    }
  };

  const handleDivisionChange = data => {
    setPayload({
      ...payload,
      divisionId: data,
      classFilter: { id: '', name: '' },
    });
    setClassData(data.children!);
  };

  const handleClassChange = data => {
    setPayload({ ...payload, classFilter: data });
  };

  const handleSaveClick = () => {
    if (payload) {
      getShowRiderPoints(
        showId ?? '',
        payload.divisionId.id,
        payload.categoryId.id,
        payload.classFilter.id ? [payload.classFilter.id] : undefined,
        sortBy,
        sortOrder,
      );
    }
  };

  const handleCancelClick = () => {
    setSortBy('team');
    setSortOrder(1);
    getShowRiderPoints(
      showId ?? '',
      undefined,
      undefined,
      undefined,
      'team',
      1,
    );
    setPayload(defaultAddToShowData);
    setClassData([]);
    setDivisionData([]);
  };

  const refreshFunction = () => {
    getShowRiderPoints(
      showId ?? '',
      payload.divisionId.id,
      payload.categoryId.id,
      payload.classFilter.id ? [payload.classFilter.id] : undefined,
      sortBy,
      sortOrder,
    );
  };

  return (
    <StyledCommonPageWrapper>
      <FilterWrapper>
        <DropdownWrapper>
          <AdminDropDown
            label="Category"
            placeholder=""
            data={getLookupArray('TEAM_CATEGORY', lookUps)}
            onDataChange={handleCategoryChange}
            defaultValue={payload.categoryId}
            isLoader={false}
          />
          <AdminDropDown
            label="Division"
            placeholder=""
            data={divisonData}
            onDataChange={handleDivisionChange}
            defaultValue={payload.divisionId}
            isLoader={false}
            disabled={divisonData === undefined || divisonData?.length === 0}
          />
          <AdminDropDown
            label="Class"
            placeholder=""
            data={classData}
            onDataChange={handleClassChange}
            defaultValue={payload.classFilter}
            isLoader={false}
            disabled={classData === undefined || classData?.length === 0}
          />
        </DropdownWrapper>
        <ActionWrapper>
          <AdminCustomButton
            buttonType="cancel"
            onClick={handleCancelClick}
            disabled={payload.categoryId.id === ''}
          >
            Cancel
          </AdminCustomButton>
          <AdminCustomButton
            buttonType="save"
            onClick={handleSaveClick}
            disabled={payload.categoryId.id === ''}
          >
            Filter Results
          </AdminCustomButton>
        </ActionWrapper>
      </FilterWrapper>

      <TitleCardWrapper>
        <HeadingWrapper className="heading-wrapper show-status">
          <PageMainLabel className="label-wrapper">
            Rider Information
          </PageMainLabel>
          <div className="point-wrapper">
            <PageMainLabel className="Point-label-wrapper">
              Total Entries : {riderShowList?.totalEntries}
            </PageMainLabel>
          </div>
        </HeadingWrapper>
      </TitleCardWrapper>

      {loading && !hasPendingChanges && !isSaving ? (
        <TableSkeletonLoader rowContent={8} width={100} headingWidth={80} />
      ) : (
        <RiderInformationTable
          riderShowList={riderShowList}
          refreshFunction={refreshFunction}
          onPointChange={handlePointChange}
          resetKey={resetKey}
          onScratchDateChange={handleScratchDateChange}
          simpleValidator={simpleValidator}
          triggerValidation={triggerValidation}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSortChange={handleSortChange}
        />
      )}
    </StyledCommonPageWrapper>
  );
};

// ── Styles (unchanged) ────────────────────────────────────────────────────────
const FilterWrapper = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  padding: 0px 0px 20px 0px;
  @media print {
    display: none;
  }
`;
const DropdownWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: start;
  width: 60%;
  gap: 15px;
  .SHdropdown {
    width: 33.33%;
  }
`;
const ActionWrapper = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  justify-content: start;
  width: 40%;
  padding-top: 22px;
  padding-left: 12px;
`;
const StyledCommonPageWrapper = styled(CommonPageWrapper)`
  padding: unset !important;
  @media print {
    width: 100% !important;
    height: auto !important;
    overflow: visible !important;
    padding: 0 !important;
  }
`;
const TitleCardWrapper = styled.div`
  margin: 0px 0px 0px 0px;
  background-color: #e7ecff;
  .heading-wrapper {
    padding: 5px 0px 5px 0px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 5px 20px;
    @media print {
      display: none;
    }

    .point-wrapper {
      display: flex;
      justify-content: center;
      width: 200px;
    }

    .label-wrapper {
      color: #000000;
      font-size: 14px;
      font-weight: 600;
      margin: unset !important;
      letter-spacing: 0.2px;
      font-family: 'inter';
    }
    .Point-label-wrapper {
      color: #000000;
      font-size: 14px;
      font-weight: 600;
      margin: unset !important;
      letter-spacing: 0.2px;
      font-family: 'inter';
    }
  }
`;
